import { userService } from '#/client/api';
import { ProfileResponse, TokenResponse } from '#/client/models';
import { StorageKeys } from '#/service/constants';
import { LS } from '#/service/storage';
import { getDeviceInfo } from '#/service/user/userSessionManager';

export async function getAnonymousToken() {
  const reqBody = {
    deviceInfo: getDeviceInfo(),
  };

  await userService.getAnonymousToken(reqBody);
}

export interface AuthTokenLs
  extends Omit<TokenResponse, 'token' | 'refreshToken'> {
  isLoggedIn: boolean;
}

export function saveAuthToken(tokenResponse: TokenResponse) {
  const { token, refreshToken, ...rest } = tokenResponse;

  const lsToken: AuthTokenLs = {
    isLoggedIn: Boolean(token && refreshToken),
    ...rest,
  };

  LS.set(StorageKeys.AUTHENTICATION, lsToken);
}

// Slack discussion: https://leyra.slack.com/archives/C0AKKR3QBKR/p1701268561275589
// HMN is obviously HMN-specific and we can ignore it in our detection logic
// Toast - I still don't know what it is, but it's applicable to the new accounts only
// and again - we can ignore it
const ignoredSubscriptionIds = ['HMN', 'Toast'];

/**
 * Logic for guessing if subscription is Web-based and as-such it can be managed
 * through the Web client
 * */
export function canManageSubscription(profile: ProfileResponse) {
  if (!profile.entitlements?.subscriber) {
    return false;
  }

  const subscriptions = (profile.entitlements.subscriptions ?? []).filter(
    ({ id }) => !ignoredSubscriptionIds.includes(id)
  );

  return subscriptions.some(
    ({ purchaseMethod }) => purchaseMethod === 'CREDIT_CARD'
  );
}

/**
 * Logic for guessing if a user was previously logged in upon initial app load to avoid unnecessary re-rendering of app.
 * */
export const guessInitialLoginFromLS = () => {
  try {
    const storedAuth = LS.get<AuthTokenLs>(StorageKeys.AUTHENTICATION);

    return !!storedAuth?.isLoggedIn;
  } catch {
    return false;
  }
};

/**
 * Updates the preferences prompt flag in localStorage so it
 * stays in sync with the store value across page reloads.
 */
export const setShouldPromptPreferencesInLS = (value: boolean) => {
  const storedAuth = LS.get<AuthTokenLs>(StorageKeys.AUTHENTICATION);

  if (storedAuth) {
    LS.set(StorageKeys.AUTHENTICATION, {
      ...storedAuth,
      preferences: { ...storedAuth.preferences, prompt: value },
    });
  }
};

/**
 * Checks if the user should be prompted to set their preferences.
 * This value is saved during login/registration and read back on page reload
 * so the prompt survives a browser refresh.
 */
export const getShouldPromptPreferencesFromLS = () => {
  const storedAuth = LS.get<AuthTokenLs>(StorageKeys.AUTHENTICATION);

  return !!storedAuth?.preferences?.prompt;
};

/**
 * Get cache key for users current entitlements.
 * @param profile User profile response
 *
 * Key consists of following three parts:
 * - User is a subscriber or not.
 * - User upgrade, downgrade or crossgrade a subscription.
 * - User purchase a new product which means a new entitlement tag.
 */
export const getEntitlementsKey = (profile: ProfileResponse) => {
  return [
    profile.subscriber,
    profile.subscriptions?.map((subscription) => subscription.id).join(','),
    profile.entitlements.grantedEntitlementTags?.join(','),
  ].join('_');
};
